import type {
    AuthenticatePaymentV1Dto,
    AvailablePaymentMethodsV1Dto,
    CreatePaymentV1Dto,
    ExecutePaymentV1Dto,
    IbanValidationRequestV1Dto,
    IbanValidationResponseV1Dto,
    PaymentConfig,
    PaymentInstrumentDetailsV1Dto,
    PaymentMethod,
    PaymentStatus,
    PaymentV1Dto,
    UserAccountPaymentInstrumentsV1Dto,
    UserPaymentSessionDetailsResponseDto
} from '../types';
import { requireResponse, responseOrDefault } from '../utils/api-response';
import { PaymentApi } from './api';
import {
    type ExecuteBlockedReason,
    reportMissingExecuteData,
    reportMissingRedirectParams,
    reportPaymentError,
    reportPollingTimeout,
    toTelemetryContext
} from './logs';
import { PaymentError } from './payment-error';
import {
    executeWithRetry,
    PollingAbortedError,
    PollingManager
} from './retry';
import {
    appendPaymentToken,
    cleanRedirectParamsFromLocation,
    getRedirectParamsFromLocation
} from './url-utils';

export interface PaymentCallbacks {
    onAction?: (actionPayload: string) => void;
    onSettled?: () => void;
    onSucceeded?: () => void;
    onError?: (error: PaymentError) => void;
}

const EXECUTE_BLOCKED_MESSAGES: Record<ExecuteBlockedReason, string> = {
    REDIRECT_RESULT_MISSING:
        'Payment cannot be executed: the payment provider redirected back without redirectResult',
    EXECUTE_DATA_NOT_PROVIDED:
        'Payment cannot be executed: the payment awaits EXECUTE but no execution data was provided'
};

class PaymentProcessor {
    private config!: PaymentConfig;
    private currentPayment: PaymentV1Dto | null = null;
    private sessionDetails: UserPaymentSessionDetailsResponseDto | null = null;
    private pollingManager: PollingManager;
    private activePaymentMethod?: PaymentMethod;
    private callbacks: PaymentCallbacks = {};
    private globalErrorHandler?: (error: PaymentError) => void;
    private paymentApi!: PaymentApi;

    constructor() {
        this.pollingManager = new PollingManager((error, context) =>
            reportPollingTimeout(
                error,
                context,
                this.config
                    ? toTelemetryContext(this.config, this.activePaymentMethod)
                    : undefined
            )
        );
    }

    public configure(config: PaymentConfig): void {
        this.config = config;

        const existingPaymentRequestToken =
            this.paymentApi?.paymentRequestToken;
        this.paymentApi = new PaymentApi(
            config.userSessionToken,
            config.environment,
            config.baseUrl,
            config.region
        );

        if (existingPaymentRequestToken) {
            this.paymentApi.setPaymentRequestToken(existingPaymentRequestToken);
        }
    }

    public getCurrentStatus(): PaymentStatus | null {
        return this.currentPayment?.status || null;
    }

    public getConfig(): PaymentConfig {
        return this.config;
    }

    public getCurrentPayment(): PaymentV1Dto | null {
        return this.currentPayment;
    }

    public getNextAction(): string | null {
        return this.currentPayment?.nextAction || null;
    }

    public reset(): void {
        this.pollingManager.reset();
        this.currentPayment = null;
    }

    public clearPaymentRequestToken(): void {
        if (this.paymentApi) {
            this.paymentApi.clearPaymentRequestToken();
        }
        this.currentPayment = null;
    }

    public hasPaymentRequestToken(): boolean {
        return !!this.paymentApi?.paymentRequestToken;
    }

    public setActivePaymentMethod(method: PaymentMethod | undefined): void {
        this.activePaymentMethod = method;
    }

    public getActivePaymentMethod(): PaymentMethod | undefined {
        return this.activePaymentMethod;
    }

    private updatePayment(payment: PaymentV1Dto, requestToken: string) {
        this.currentPayment = payment;

        if (payment?.status === 'FAILED' || payment?.status === 'CANCELED') {
            if (this.paymentApi?.paymentRequestToken === requestToken) {
                this.clearPaymentRequestToken();
            }
            throw new PaymentError(
                this.getErrorMessageForStatus(
                    payment.status,
                    payment.errorDescription
                ),
                payment.status === 'CANCELED'
                    ? 'PAYMENT_CANCELED'
                    : 'PAYMENT_FAILED'
            );
        }

        if (payment?.errorDescription && payment.status !== 'PREAUTHED') {
            throw new PaymentError(payment.errorDescription, 'API_ERROR');
        }
    }

    private getErrorMessageForStatus(
        status: PaymentStatus,
        errorDescription?: string
    ): string {
        if (errorDescription?.trim()) {
            return errorDescription;
        }

        if (status === 'CANCELED') {
            return 'The payment was cancelled. Please try again.';
        }

        return 'Something went wrong. Please try again.';
    }

    public async getAvailablePaymentMethods(): Promise<
        AvailablePaymentMethodsV1Dto[]
    > {
        const methods = await executeWithRetry(() => {
            return this.paymentApi.client.GetAvailablePaymentMethods.getAvailablePaymentMethods();
        });
        return responseOrDefault(methods, []);
    }

    public async getUserAccountPaymentInstruments(): Promise<UserAccountPaymentInstrumentsV1Dto> {
        const instruments = await executeWithRetry(() => {
            return this.paymentApi.client.GetUserAccountPaymentInstruments.getUserAccountPaymentInstruments();
        });
        return responseOrDefault(instruments, { paymentInstruments: [] });
    }

    public setPaymentRequestToken(token: string): void {
        this.paymentApi.setPaymentRequestToken(token);
    }

    public setGlobalErrorHandler(
        errorHandler: (error: PaymentError) => void
    ): void {
        this.globalErrorHandler = errorHandler;
    }

    public getUserPaymentSessionDetails(): Promise<UserPaymentSessionDetailsResponseDto> {
        return executeWithRetry(() => {
            return this.paymentApi.client.GetUserPaymentSessionDetails.getUserPaymentSessionDetails();
        });
    }

    public setSessionDetails(
        sessionDetails: UserPaymentSessionDetailsResponseDto
    ): void {
        this.sessionDetails = sessionDetails;
    }

    public getSessionMoney() {
        return this.sessionDetails?.money;
    }

    public getSessionScope() {
        return this.sessionDetails?.scope;
    }

    public async validateIban(
        dto: IbanValidationRequestV1Dto
    ): Promise<IbanValidationResponseV1Dto> {
        const response = await executeWithRetry(() =>
            this.paymentApi.client.ValidateIban.validateIban(dto)
        );
        return requireResponse(response);
    }

    public async createPaymentRequest(): Promise<string> {
        const response = await executeWithRetry(() => {
            return this.paymentApi.client.CreatePaymentRequest.createPaymentRequest();
        });
        const paymentRequest = requireResponse(response);

        if (paymentRequest.token) {
            this.paymentApi.setPaymentRequestToken(paymentRequest.token);
            return paymentRequest.token;
        }

        throw new PaymentError(
            'No token received from payment request API',
            'API_ERROR'
        );
    }

    public async initiatePayment(
        dto: CreatePaymentV1Dto
    ): Promise<PaymentV1Dto> {
        const { paymentRequestToken } = this.paymentApi;
        if (!paymentRequestToken) {
            throw new PaymentError(
                'Payment request token is required for payment operations',
                'VALIDATION_ERROR'
            );
        }

        const response = await executeWithRetry(() => {
            return this.paymentApi.client.CreatePayment.createPayment(
                paymentRequestToken,
                dto
            );
        });
        const payment = requireResponse(response);
        this.updatePayment(payment, paymentRequestToken);
        return payment;
    }

    public initiateAndAwaitCreation = async (
        paymentMethod: PaymentMethod
    ): Promise<PaymentV1Dto> => {
        const payment = await this.initiatePayment({ paymentMethod });

        if (payment.status !== 'AWAIT_CREATE_RESULT') {
            return payment;
        }

        return this.pollUntilResolved('AWAIT_CREATE_RESULT');
    };

    private async authenticatePayment(
        dto?: AuthenticatePaymentV1Dto
    ): Promise<void> {
        const { paymentRequestToken } = this.paymentApi;
        if (!paymentRequestToken) {
            throw new PaymentError(
                'Payment request token is required for authentication',
                'VALIDATION_ERROR'
            );
        }

        await executeWithRetry(() => {
            const authDto: AuthenticatePaymentV1Dto = {
                ...dto,
                paymentMethodDetails: dto?.paymentMethodDetails ?? '{}',
                returnUrl: this.buildReturnUrl(),
                origin: window.location.origin,
                channel: 'WEB',
                storePaymentMethod: true
            };
            return this.paymentApi.client.AuthenticatePayment.authenticatePayment(
                paymentRequestToken,
                authDto
            );
        });
    }

    public async executePayment(
        dto: ExecutePaymentV1Dto
    ): Promise<void> {
        const { paymentRequestToken } = this.paymentApi;
        if (!paymentRequestToken) {
            throw new PaymentError(
                'Payment request token is required for payment execution',
                'VALIDATION_ERROR'
            );
        }

        await executeWithRetry(() => {
            return this.paymentApi.client.ExecutePayment.executePayment(
                paymentRequestToken,
                dto
            );
        });
    }

    public async getPayment(
        signal?: AbortSignal
    ): Promise<PaymentV1Dto | undefined> {
        const { paymentRequestToken } = this.paymentApi;
        if (!paymentRequestToken) {
            throw new PaymentError(
                'Payment request token is required to get payment status',
                'VALIDATION_ERROR'
            );
        }

        const payment = await executeWithRetry(() => {
            return this.paymentApi.client.GetPayment.getPayment(
                paymentRequestToken
            );
        });
        if (!payment || signal?.aborted) {
            return undefined;
        }

        this.updatePayment(payment, paymentRequestToken);
        return payment;
    }

    public pollUntilResolved(
        waitingStatus: PaymentStatus
    ): Promise<PaymentV1Dto> {
        return new Promise<PaymentV1Dto>((resolve, reject) => {
            const signal = this.pollingManager.getSignal();

            const rejectAsAborted = () => {
                reject(new PollingAbortedError());
            };
            signal.addEventListener('abort', rejectAsAborted, { once: true });

            const settle = (complete: () => void) => {
                signal.removeEventListener('abort', rejectAsAborted);
                this.reset();
                complete();
            };

            const handleError = (err: unknown) => {
                if (signal.aborted) {
                    return;
                }
                settle(() => reject(this.handleError(err)));
            };

            const poll = async () => {
                try {
                    const payment = await this.getPayment(signal);

                    if (signal.aborted) {
                        return;
                    }

                    if (payment && payment.status !== waitingStatus) {
                        settle(() => resolve(payment));
                        return;
                    }

                    this.pollingManager.schedule(poll, handleError, signal);
                } catch (error) {
                    handleError(error);
                }
            };

            this.pollingManager.schedule(poll, handleError, signal);
        });
    }

    public handleError(error: unknown): PaymentError {
        const paymentError = PaymentError.fromUnknown(error);

        if (!paymentError.paymentMethod && this.activePaymentMethod) {
            paymentError.paymentMethod = this.activePaymentMethod;
        }

        return paymentError;
    }

    /**
     * Get payment instrument details for a specific payment request token
     */
    public async getPaymentInstrumentDetails(
        paymentRequestToken: string
    ): Promise<
        | {
              details?: PaymentInstrumentDetailsV1Dto;
              token?: string;
              fingerprint?: string;
          }
        | undefined
    > {
        try {
            const { paymentInstruments } =
                await this.getUserAccountPaymentInstruments();

            const matchingInstrument = paymentInstruments.find(
                (instrument) =>
                    instrument.paymentRequestToken === paymentRequestToken
            );

            if (!matchingInstrument) {
                return undefined;
            }

            const { details, fingerprint } = matchingInstrument;

            return {
                details,
                token: matchingInstrument.paymentInstrumentToken,
                fingerprint
            };
        } catch (error) {
            reportPaymentError(
                this.handleError(error),
                toTelemetryContext(this.config, this.activePaymentMethod)
            );
            return undefined;
        }
    }

    public pausePolling() {
        this.pollingManager.reset();
    }

    private async poll(
        authData?: AuthenticatePaymentV1Dto,
        executeData?: ExecutePaymentV1Dto,
        missingExecuteDataReason: ExecuteBlockedReason = 'EXECUTE_DATA_NOT_PROVIDED'
    ): Promise<void> {
        const signal = this.pollingManager.getSignal();

        try {
            const payment = await this.getPayment(signal);

            if (signal.aborted) {
                return;
            }

            if (!payment) {
                this.schedulePolling(
                    signal,
                    authData,
                    executeData,
                    missingExecuteDataReason
                );
                return;
            }

            const { onAction } = this.callbacks;

            if (
                payment.actionPayload &&
                onAction &&
                (payment.status !== 'PREAUTHED' ||
                    this.activePaymentMethod === 'BACS')
            ) {
                this.pausePolling();
                this.handOffAction(onAction, payment.actionPayload);
                return;
            }

            if (payment.status === 'PREAUTHED') {
                this.pausePolling();
                this.callbacks.onSettled?.();
                await this.completePreauthorizedPayment(payment);
                return;
            }

            await this.handleNextAction(
                payment,
                authData,
                executeData,
                missingExecuteDataReason
            );

            this.schedulePolling(
                signal,
                authData,
                executeData,
                missingExecuteDataReason
            );
        } catch (error) {
            if (signal.aborted) {
                return;
            }
            this.failPayment(error);
        }
    }

    private handOffAction = (
        onAction: (actionPayload: string) => void,
        actionPayload: string
    ): void => {
        try {
            onAction(actionPayload);
        } catch (error) {
            this.failPayment(error);
        }
    };

    private failPayment(error: unknown): void {
        this.pausePolling();
        this.callbacks.onSettled?.();

        const paymentError = this.handleError(error);
        const onError = this.callbacks.onError ?? this.globalErrorHandler;

        if (!onError) {
            throw paymentError;
        }

        onError(paymentError);
    }

    private async handleNextAction(
        payment: PaymentV1Dto,
        authData: AuthenticatePaymentV1Dto | undefined,
        executeData: ExecutePaymentV1Dto | undefined,
        missingExecuteDataReason: ExecuteBlockedReason
    ): Promise<void> {
        const action = payment.nextAction;

        if (action === 'AUTHENTICATE') {
            await this.authenticatePayment(authData);
            return;
        }

        if (action === 'EXECUTE') {
            if (!executeData) {
                const error = new PaymentError(
                    EXECUTE_BLOCKED_MESSAGES[missingExecuteDataReason],
                    'PAYMENT_INCOMPLETE'
                );
                reportMissingExecuteData(
                    error,
                    {
                        reason: missingExecuteDataReason,
                        paymentStatus: payment.status,
                        nextAction: payment.nextAction,
                        hasActionPayload: Boolean(payment.actionPayload)
                    },
                    toTelemetryContext(this.config, this.activePaymentMethod)
                );
                throw error;
            }
            await this.executePayment(executeData);
        }
    }

    private async completePreauthorizedPayment(
        payment: PaymentV1Dto
    ): Promise<void> {
        const token =
            payment.paymentRequestToken ||
            this.paymentApi.paymentRequestToken ||
            '';

        if (!token) {
            throw new PaymentError(
                'Payment was preauthorized without a payment request token',
                'API_ERROR'
            );
        }

        this.clearPaymentRequestToken();
        this.callbacks.onSucceeded?.();

        const instrumentData = await this.getPaymentInstrumentDetails(token);

        this.config.onSuccess?.(
            token,
            instrumentData?.details,
            instrumentData?.token,
            instrumentData?.fingerprint
        );
    }

    public async processPayment(
        authData?: AuthenticatePaymentV1Dto,
        executeData?: ExecutePaymentV1Dto,
        callbacks: PaymentCallbacks = {},
        missingExecuteDataReason?: ExecuteBlockedReason
    ): Promise<void> {
        this.callbacks = callbacks;
        await this.poll(authData, executeData, missingExecuteDataReason);
    }

    private schedulePolling(
        signal: AbortSignal,
        authData: AuthenticatePaymentV1Dto | undefined,
        executeData: ExecutePaymentV1Dto | undefined,
        missingExecuteDataReason: ExecuteBlockedReason
    ) {
        this.pollingManager.schedule(
            () => this.poll(authData, executeData, missingExecuteDataReason),
            (error) => this.failPayment(error),
            signal
        );
    }

    public buildReturnUrl(): string {
        const { paymentRequestToken } = this.paymentApi;
        const base =
            this.config?.returnUrl ||
            cleanRedirectParamsFromLocation(window.location);

        return appendPaymentToken(base, paymentRequestToken);
    }

    public hasConfiguredReturnUrl(): boolean {
        return Boolean(this.config?.returnUrl);
    }

    public async handleRedirect(
        onProcessingStart?: () => void,
        onProcessingComplete?: () => void,
        onRedirectSuccess?: () => void
    ): Promise<void> {
        const { search, hash } = window.location;
        const {
            redirectResult,
            paymentToken,
            hasRedirectParameters,
            redirectResultInQuery,
            paymentTokenInQuery,
            hasHashRoute
        } = getRedirectParamsFromLocation(search, hash);

        if (hasRedirectParameters && (!redirectResult || !paymentToken)) {
            reportMissingRedirectParams(
                {
                    missingRedirectResult: !redirectResult,
                    missingPaymentToken: !paymentToken,
                    redirectResultInQuery,
                    paymentTokenInQuery,
                    hasHashRoute
                },
                toTelemetryContext(this.config, this.activePaymentMethod)
            );
        }

        if (hasRedirectParameters) {
            window.history.replaceState(
                {},
                '',
                cleanRedirectParamsFromLocation(window.location)
            );
        }

        if (!redirectResult && !paymentToken) {
            return;
        }

        if (paymentToken) {
            this.paymentApi.setPaymentRequestToken(paymentToken);
        }

        onProcessingStart?.();

        await this.processPayment(
            undefined,
            redirectResult ? { redirectResult } : undefined,
            {
                onSettled: onProcessingComplete,
                onSucceeded: onRedirectSuccess
            },
            'REDIRECT_RESULT_MISSING'
        );
    }
}

export { PaymentProcessor };
